import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Hook para gerenciar busca dentro do conteúdo
 * Funciona como Ctrl+F do navegador
 */
const useContentSearch = (containerRef, isModalOpen = true) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [matches, setMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const highlightedElements = useRef([]);
  const originalContents = useRef(new Map());

  // Limpar highlights anteriores
  const clearHighlights = useCallback(() => {
    // Use requestAnimationFrame to batch DOM updates
    requestAnimationFrame(() => {
      highlightedElements.current.forEach(el => {
        const originalContent = originalContents.current.get(el);
        if (originalContent !== undefined) {
          el.innerHTML = originalContent;
        }
      });
      highlightedElements.current = [];
      originalContents.current.clear();
    });
  }, []);

  // Scroll para um match específico
  const scrollToMatch = useCallback((index) => {
    // Use requestAnimationFrame to prevent ResizeObserver errors during scroll
    requestAnimationFrame(() => {
      const marks = document.querySelectorAll('.search-highlight');
      if (marks.length === 0 || index < 1 || index > marks.length) return;

      // Remover highlight ativo anterior
      marks.forEach(mark => {
        mark.style.backgroundColor = '#ffeb3b';
      });

      // Destacar o match atual
      const currentMark = marks[index - 1];
      if (currentMark) {
        currentMark.style.backgroundColor = '#ff9800';
        currentMark.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    });
  }, []);

  // Aplicar highlights no texto
  const applyHighlights = useCallback((term) => {
    if (!containerRef?.current || !term.trim()) {
      clearHighlights();
      setMatches([]);
      return;
    }

    clearHighlights();

    // Use requestAnimationFrame to batch DOM updates and prevent ResizeObserver errors
    requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;

      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            // Ignorar scripts, styles e elementos já highlightados pela BUSCA
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;
            const tagName = parent.tagName.toLowerCase();

            // Ignorar tags estruturais/invisíveis
            if (['script', 'style', 'noscript'].includes(tagName)) {
              return NodeFilter.FILTER_REJECT;
            }

            // Se for <mark>, só ignorar se for highlight DE BUSCA (.search-highlight)
            // Permite buscar dentro de .briefing-highlight ou .stored-highlight
            if (tagName === 'mark' && parent.classList.contains('search-highlight')) {
              return NodeFilter.FILTER_REJECT;
            }

            // Verificar se contém o termo
            if (node.textContent.toLowerCase().includes(term.toLowerCase())) {
              return NodeFilter.FILTER_ACCEPT;
            }
            return NodeFilter.FILTER_REJECT;
          }
        }
      );

      const textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        textNodes.push(node);
      }

      const newMatches = [];
      const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');

      // Batch all DOM updates together
      textNodes.forEach(textNode => {
        const parent = textNode.parentElement;
        // Só pular se for pai <mark> QUE SEJA da busca 
        if (!parent || (parent.tagName === 'MARK' && parent.classList.contains('search-highlight'))) return;

        const text = textNode.textContent;
        if (!regex.test(text)) return;
        regex.lastIndex = 0; // Reset regex

        // Salvar conteúdo original
        if (!originalContents.current.has(parent)) {
          originalContents.current.set(parent, parent.innerHTML);
          highlightedElements.current.push(parent);
        }

        // Substituir com highlights
        const highlightedHtml = text.replace(regex, (match, index) => {
          const matchIndex = newMatches.length;
          newMatches.push({ element: parent, text: match });
          return `<mark class="search-highlight" data-match-index="${matchIndex}" style="background-color: #ffeb3b; padding: 1px 2px; border-radius: 2px;">${match}</mark>`;
        });

        // Criar um span temporário para substituir
        const span = document.createElement('span');
        span.innerHTML = highlightedHtml;

        // Substituir o nó de texto
        parent.replaceChild(span, textNode);
      });

      setMatches(newMatches);
      if (newMatches.length > 0) {
        setCurrentMatchIndex(1);
        // Scroll para o primeiro match usando requestAnimationFrame
        requestAnimationFrame(() => {
          scrollToMatch(1);
        });
      } else {
        setCurrentMatchIndex(0);
      }
    });
  }, [containerRef, clearHighlights, scrollToMatch]);

  // Navegação
  const goToNextMatch = useCallback(() => {
    if (matches.length === 0) return;
    const newIndex = currentMatchIndex >= matches.length ? 1 : currentMatchIndex + 1;
    setCurrentMatchIndex(newIndex);
    scrollToMatch(newIndex);
  }, [matches.length, currentMatchIndex, scrollToMatch]);

  const goToPrevMatch = useCallback(() => {
    if (matches.length === 0) return;
    const newIndex = currentMatchIndex <= 1 ? matches.length : currentMatchIndex - 1;
    setCurrentMatchIndex(newIndex);
    scrollToMatch(newIndex);
  }, [matches.length, currentMatchIndex, scrollToMatch]);

  // Abrir/fechar busca
  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchTerm('');
    clearHighlights();
    setMatches([]);
    setCurrentMatchIndex(0);
  }, [clearHighlights]);

  // Detectar Ctrl+F
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        openSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, openSearch]);

  // Aplicar highlights quando o termo muda
  useEffect(() => {
    if (searchTerm) {
      const debounceTimer = setTimeout(() => {
        applyHighlights(searchTerm);
      }, 300);
      return () => {
        clearTimeout(debounceTimer);
      };
    } else {
      // Clear highlights immediately when search term is empty
      requestAnimationFrame(() => {
        clearHighlights();
        setMatches([]);
        setCurrentMatchIndex(0);
      });
    }
  }, [searchTerm, applyHighlights, clearHighlights]);

  // Limpar ao fechar o modal
  useEffect(() => {
    if (!isModalOpen) {
      closeSearch();
    }
  }, [isModalOpen, closeSearch]);

  return {
    isSearchOpen,
    searchTerm,
    setSearchTerm,
    totalMatches: matches.length,
    currentMatch: currentMatchIndex,
    openSearch,
    closeSearch,
    goToNextMatch,
    goToPrevMatch
  };
};

export default useContentSearch;
